/**
 * SoleadLab 2.0 - Editor Page
 *
 * Dual nav-rail layout:
 * [Left Rail 72px] [Left Ticket 280px] [Map] [Right Ticket 280px] [Right Rail 72px]
 *
 * Left rail  → Inspection Editor Panel
 * Right rail → Monitoring Builder Panel
 * Layers panel → always floating over map
 */

import { useState, useEffect, useRef } from 'react';
import { humanEditProvenance, topologyValuesEqual } from '../domain/topology/applyDerivedTopology';
import { useNavigate } from 'react-router-dom';
import { useProjectStore, useMapStore, useUIStore, useMonitoringStore } from '../stores';
import { useAuthStore } from '../stores/authStore';
// Auto-save removed — editor runs locally, user saves explicitly via "Save to Server"
import { EditorPanel, IndicationPopup, IndicationPreview, PhotoViewerModal } from '../components/editor';
import FieldActionsPanel from '../components/editor/FieldActionsPanel';
import FieldAddControl from '../components/editor/FieldAddControl';
import type { IndicationFormData } from '../components/editor';
import { MonitoringPanel } from '../components/monitoring';
import DataPointConfigModal from '../components/monitoring/DataPointConfigModal';
import AdvancedRuleModal from '../components/monitoring/AdvancedRuleModal';
import SLDViewerModal from '../components/monitoring/SLDViewerModal';
import ProjectSelectModal from '../components/common/ProjectSelectModal';
import DriveImportModal from '../components/editor/DriveImportModal';
import StorageAdminModal from '../components/admin/StorageAdminModal';
import LayoutImportModal from '../components/layout/LayoutImportModal';
import ClassificationConfigModal from '../components/layout/ClassificationConfigModal';
import PhotoRegistrationWizard from '../components/editor/PhotoRegistrationWizard';
import LayoutAnchorMode from '../components/layout/LayoutAnchorMode';
import OverlayAnchorMode from '../components/editor/OverlayAnchorMode';
import LayoutLabelPopup from '../components/layout/LayoutLabelPopup';
import ProgressOverlay from '../components/common/ProgressOverlay';
import ConfirmDialog from '../components/common/ConfirmDialog';
import NotificationToasts from '../components/common/NotificationToasts';
import { MapCanvas, LayersPanel, DateFilterToolbar } from '../components/map';
import TasksPanel from '../components/tasks/TasksPanel';
import CalculatorPanel from '../components/calculator/CalculatorPanel';
import MobileUploadsPanel from '../components/editor/MobileUploadsPanel';
import PhotoClassificationPanel from '../components/editor/PhotoClassificationPanel';
import TeamPanel from '../components/editor/TeamPanel';
import OrgSettingsPanel from '../components/editor/OrgSettingsPanel';
import ShareLinkManagerPanel from '../components/share/ShareLinkManagerPanel';
import DockMissionDemoPanel from '../components/dock-demo/DockMissionDemoPanel';
import DockStationsDemoPanel from '../components/dock-demo/DockStationsDemoPanel';
import DockHomeLayer from '../components/dock-demo/DockHomeLayer';
import DockMissionRouteLayer from '../components/dock-demo/DockMissionRouteLayer';
import DemoMissionMapLayer from '../components/dock-demo/DemoMissionMapLayer';
import DemoPhotoPopup from '../components/dock-demo/DemoPhotoPopup';
import LiveViewMockup from '../components/dock-demo/LiveViewMockup';
import MissionFlowController from '../components/dock-demo/MissionFlowController';
import DockMissionStatusBadge from '../components/dock-demo/DockMissionStatusBadge';
import MissionAutoImportController from '../components/dock-demo/MissionAutoImportController';
import DockDemoProjectScopeController from '../components/dock-demo/DockDemoProjectScopeController';
import LegOverlayLayer from '../components/dock-demo/LegOverlayLayer';
import NoFlyZoneLayer from '../components/dock-demo/NoFlyZoneLayer';
import WaypointContextMenu from '../components/dock-demo/WaypointContextMenu';
import WaypointMapEditor from '../components/dock-demo/WaypointMapEditor';
import ScanDraftLayer from '../components/dock-demo/ScanDraftLayer';
import CameraCoverageLayer from '../components/dock-demo/CameraCoverageLayer';
import IVPanel from '../components/iv/IVPanel';
import StatePanel from '../components/state/StatePanel';
import ScorePanel from '../components/scoring/ScorePanel';
import CopilotPanel from '../components/copilot/CopilotPanel';
import DetectionPanel from '../components/detection/DetectionPanel';
import { centroidOfPolygon, createManualIndication } from '../utils';
import { fetchProject, saveProject } from '../services/projectApi';
import useTileStatusPoller from '../hooks/useTileStatusPoller';
import useOverlayBakePoller from '../hooks/useOverlayBakePoller';
import useDxfIngestPoller from '../hooks/useDxfIngestPoller';
import useDetectionPoller from '../hooks/useDetectionPoller';
import useAssetLoadingProgress from '../hooks/useAssetLoadingProgress';
import { useIsReadOnlyViewer, isRailItemVisibleForShareViewer, type RailItemId } from '../hooks/useShareViewer';
import { useShareSessionStore } from '../stores/shareSessionStore';
import AssetLoadingBar from '../components/common/AssetLoadingBar';
import DxfIngestBanner from '../components/editor/DxfIngestBanner';
import logoImg from '../assets/logo.png';
import './EditorPage.css';

// ─── SVG Icon components ──────────────────────────────────────────────────────


// Left rail icons
function IconProject() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

function IconMapPin() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

function IconOrtho() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M6.3 6.3a8 8 0 0 0 0 11.4"/>
      <path d="M17.7 6.3a8 8 0 0 1 0 11.4"/>
      <path d="M3.7 3.7a12 12 0 0 0 0 16.6"/>
      <path d="M20.3 3.7a12 12 0 0 1 0 16.6"/>
    </svg>
  );
}

function IconPlans() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="3" y1="15" x2="21" y2="15"/>
      <line x1="9" y1="9" x2="9" y2="21"/>
    </svg>
  );
}

function IconCamera() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  );
}

function IconFlag() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
      <line x1="4" y1="22" x2="4" y2="15"/>
    </svg>
  );
}

function IconCalculator() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2"/>
      <line x1="8" y1="6" x2="16" y2="6"/>
      <line x1="8" y1="10" x2="10" y2="10"/>
      <line x1="14" y1="10" x2="16" y2="10"/>
      <line x1="8" y1="14" x2="10" y2="14"/>
      <line x1="14" y1="14" x2="16" y2="14"/>
      <line x1="8" y1="18" x2="10" y2="18"/>
      <line x1="14" y1="18" x2="16" y2="18"/>
    </svg>
  );
}

function IconTasks() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  );
}

// Right rail icons
function IconDatabase() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  );
}

function IconBarChart() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  );
}

function IconBell() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function IconSLD() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="6" height="6" rx="1"/>
      <rect x="16" y="2" width="6" height="6" rx="1"/>
      <rect x="9" y="16" width="6" height="6" rx="1"/>
      <line x1="5" y1="8" x2="5" y2="12"/>
      <line x1="19" y1="8" x2="19" y2="12"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
      <line x1="12" y1="12" x2="12" y2="16"/>
    </svg>
  );
}

function IconRefreshCw() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10"/>
      <polyline points="23 20 23 14 17 14"/>
      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
    </svg>
  );
}

function IconSun() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function IconMoon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

function IconUsers() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}

function IconShield() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}

function IconAdminPanel() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="9" y1="21" x2="9" y2="9"/>
    </svg>
  );
}

function IconBuilding() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2"/>
      <line x1="9" y1="6" x2="9" y2="6"/>
      <line x1="15" y1="6" x2="15" y2="6"/>
      <line x1="9" y1="10" x2="9" y2="10"/>
      <line x1="15" y1="10" x2="15" y2="10"/>
      <line x1="9" y1="14" x2="9" y2="14"/>
      <line x1="15" y1="14" x2="15" y2="14"/>
      <path d="M10 22v-4h4v4"/>
    </svg>
  );
}

function IconShareLink() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5"/>
      <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"/>
    </svg>
  );
}

function IconDrone() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="6" height="6" rx="1"/>
      <circle cx="5" cy="5" r="2.5"/>
      <circle cx="19" cy="5" r="2.5"/>
      <circle cx="5" cy="19" r="2.5"/>
      <circle cx="19" cy="19" r="2.5"/>
      <line x1="7" y1="7" x2="9.5" y2="9.5"/>
      <line x1="17" y1="7" x2="14.5" y2="9.5"/>
      <line x1="7" y1="17" x2="9.5" y2="14.5"/>
      <line x1="17" y1="17" x2="14.5" y2="14.5"/>
    </svg>
  );
}

function IconDockSettings() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {/* Dock cabinet */}
      <rect x="3" y="9" width="18" height="11" rx="1.5"/>
      {/* Drone landing pad inside */}
      <line x1="7" y1="14" x2="17" y2="14"/>
      <circle cx="12" cy="14" r="1.6"/>
      {/* Antenna */}
      <line x1="12" y1="9" x2="12" y2="5"/>
      <circle cx="12" cy="4" r="1.2"/>
      {/* Settings cog hint — small gear teeth on the side */}
      <line x1="20" y1="6" x2="22" y2="6"/>
      <line x1="20" y1="3.5" x2="21.5" y2="4.5"/>
      <line x1="20" y1="8.5" x2="21.5" y2="7.5"/>
    </svg>
  );
}

function IconIVCurve() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 20 8 16 12 17 16 8 20 4"/>
      <line x1="4" y1="4" x2="4" y2="20"/>
      <line x1="4" y1="20" x2="20" y2="20"/>
    </svg>
  );
}

function IconVectorLayout() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="13" y2="17"/>
    </svg>
  );
}

function IconCopilot() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconSiteState() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );
}

function IconReviewTools() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="15" x2="15" y2="15"/>
      <line x1="9" y1="11" x2="13" y2="11"/>
    </svg>
  );
}

function IconRadar() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {/* Radar sweep: concentric arcs + sweep line + blip */}
      <path d="M19.07 4.93A10 10 0 1 1 4.93 4.93" />
      <path d="M15.9 8.1A5.5 5.5 0 1 1 8.1 8.1" />
      <line x1="12" y1="12" x2="19.07" y2="4.93" />
      <circle cx="9.5" cy="14.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

// ─── Rail data ────────────────────────────────────────────────────────────────

type LeftTicketId = 'project' | 'map' | 'orthophoto' | 'plans' | 'vector-layout' | 'media' | 'indications' | 'calculator' | 'tasks' | 'iv-reporting' | 'site-state' | 'review-tools' | 'copilot';
type RightTicketId = 'data-source' | 'data-points' | 'alarms' | 'sld' | 'server-sync' | 'detection' | 'dock-mission' | 'dock-settings';

const LEFT_RAIL: Array<{ id: LeftTicketId; icon: React.ReactNode; label: string }> = [
  { id: 'project',     icon: <IconProject />,    label: 'Project' },
  { id: 'map',         icon: <IconMapPin />,      label: 'Map' },
  { id: 'orthophoto',  icon: <IconOrtho />,       label: 'Orthophoto' },
  { id: 'plans',          icon: <IconPlans />,        label: 'Plans Layout' },
  { id: 'vector-layout',  icon: <IconVectorLayout />, label: 'Vector Layout' },
  { id: 'media',          icon: <IconCamera />,       label: 'Media' },
  { id: 'indications', icon: <IconFlag />,        label: 'Indications' },
  { id: 'calculator',  icon: <IconCalculator />,  label: 'Calculator' },
  { id: 'tasks',       icon: <IconTasks />,       label: 'Tasks' },
  { id: 'iv-reporting', icon: <IconIVCurve />,    label: 'IV Reporting' },
  { id: 'site-state',   icon: <IconSiteState />,  label: 'Site State' },
  { id: 'review-tools', icon: <IconReviewTools />, label: 'Review Tools' },
  { id: 'copilot',      icon: <IconCopilot />,    label: 'Copilot' },
];

const RIGHT_RAIL: Array<{ id: RightTicketId; icon: React.ReactNode; label: string }> = [
  { id: 'data-source',   icon: <IconDatabase />,   label: 'Data Source' },
  { id: 'data-points',   icon: <IconBarChart />,   label: 'Data Points' },
  { id: 'alarms',        icon: <IconBell />,       label: 'Alarms' },
  { id: 'sld',           icon: <IconSLD />,        label: 'Single Line Diagram' },
  { id: 'server-sync',   icon: <IconRefreshCw />,  label: 'Server Sync' },
];

// ─── Rail item button ─────────────────────────────────────────────────────────

function RailItem({
  icon, label, active, onClick, badge,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      className={`rail-item${active ? ' rail-item--active' : ''}`}
      onClick={onClick}
      title={label}
      type="button"
    >
      <span className="rail-item__icon">{icon}</span>
      {badge != null && badge > 0 && (
        <span className="rail-item__badge">{badge > 99 ? '99+' : badge}</span>
      )}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EditorPage() {
  const navigate = useNavigate();
  // projectStore: data selectors (fine-grained to avoid full-tree re-renders)
  const meta = useProjectStore((s) => s.meta);
  const indications = useProjectStore((s) => s.data.indications);
  // projectStore: action selectors (stable refs)
  const updateIndication = useProjectStore((s) => s.updateIndication);
  const removeIndication = useProjectStore((s) => s.removeIndication);

  // Tile generation status polling
  useTileStatusPoller();

  // Plan-bake status polling (PDF overlays → full-resolution tiles)
  useOverlayBakePoller();

  // DXF ingest polling (rung 3 — companion DXF → per-layer vector data)
  useDxfIngestPoller();

  // Unsaved-work refresh guard. Vector layouts (entities + anchors) persist
  // ONLY via explicit Save to Server — a refresh with the Modified badge lit
  // silently destroyed an anchored layout three times in two days (twice the
  // owner, once on an assistant-instructed refresh) before this existed. The
  // browser's native "leave site?" dialog is the whole feature; entity-backed
  // state (overlays, indications) survives either way, this guards the blob.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (useProjectStore.getState().meta.isModified) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires a set returnValue to show the dialog
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Fault-detection job polling (P2a) — single mount; the Detection ticket
  // (right rail) dispatches, the MapCanvas ghost overlay consumes the
  // populated candidates.
  useDetectionPoller();

  // Asset loading progress (non-blocking bar)
  const assetLoading = useAssetLoadingProgress();

  // mapStore
  const isDrawing = useMapStore((s) => s.isDrawing);
  const drawMode = useMapStore((s) => s.drawMode);

  // uiStore: data selectors
  const activeModal = useUIStore((s) => s.activeModal);
  const modalData = useUIStore((s) => s.modalData);
  const leftActiveTicket = useUIStore((s) => s.leftActiveTicket);
  const rightActiveTicket = useUIStore((s) => s.rightActiveTicket);
  const showProjectSelect = useUIStore((s) => s.showProjectSelect);
  // uiStore: action selectors (stable refs)
  const openModal = useUIStore((s) => s.openModal);
  const closeModal = useUIStore((s) => s.closeModal);
  const openPanel = useUIStore((s) => s.openPanel);
  const closePanel = useUIStore((s) => s.closePanel);
  const panelStates = useUIStore((s) => s.panelStates);
  const addNotification = useUIStore((s) => s.addNotification);
  const setLeftActiveTicket = useUIStore((s) => s.setLeftActiveTicket);
  const setRightActiveTicket = useUIStore((s) => s.setRightActiveTicket);
  const setShowProjectSelect = useUIStore((s) => s.setShowProjectSelect);
  const layoutAnchorMode = useUIStore((s) => s.layoutAnchorMode);
  const overlayAnchorMode = useUIStore((s) => s.overlayAnchorMode);

  // monitoringStore
  const activeAlarms = useMonitoringStore((s) => s.activeAlarms);
  const triggeredAdvancedRules = useMonitoringStore((s) => s.triggeredAdvancedRules);

  // Auth state
  const authUser = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const currentOrg = useAuthStore(s => s.currentOrg);
  const impersonator = useAuthStore(s => s.impersonator);
  const stopImpersonation = useAuthStore(s => s.stopImpersonation);
  const isAdmin = authUser?.role === 'admin';
  const isImpersonating = impersonator !== null;
  // F-02E-1: surfaces the View-only share links rail button. Mirrors the
  // backend gate in soleadlab-api/src/handlers/shareLinks.ts → isAuthorized(
  // ctx, ['admin', 'operator']), which passes for platform-admin OR org
  // owner/admin/operator. Pure UI gate — backend remains the source of truth.
  const canManageShareLinks =
    isAdmin ||
    currentOrg?.role === 'owner' ||
    currentOrg?.role === 'admin' ||
    currentOrg?.role === 'operator';

  // Fault-detection rail gating — identical predicate to the retired
  // floating ScanButton: UI mirror of the backend gate in soleadlab-api
  // handlers/detections.ts → isAuthorized(['admin','operator']), which
  // passes for platform-admin OR org owner/admin/operator. Share viewers
  // additionally never see the rail item (isRailVisible below).
  const canRunDetection =
    isAdmin ||
    currentOrg?.role === 'owner' ||
    currentOrg?.role === 'admin' ||
    currentOrg?.role === 'operator';

  // F-02E-2B: read-only share-viewer mode. Used to filter rail items that
  // are pure owner surfaces (Project / Media / Copilot) and to gate map
  // click → add-indication modal.
  const isReadOnly = useIsReadOnlyViewer();

  // Mobile rails collapse (owner phone pass 2026-08-12): on narrow screens
  // the rails tuck to the side edges with a small arrow toggle — field techs
  // get the map. Desktop starts open; phones start collapsed.
  const [leftRailOpen, setLeftRailOpen] = useState<boolean>(
    () => typeof window === 'undefined' || window.innerWidth > 560,
  );
  const [rightRailOpen, setRightRailOpen] = useState<boolean>(
    () => typeof window === 'undefined' || window.innerWidth > 560,
  );
  // F-02E-2C: share-viewer's exposed-section snapshot for rail filtering.
  // Subscribing once here gives every rail-visibility call a stable view
  // reference and keeps the filter logic out of EditorPage's render path.
  const shareView = useShareSessionStore((s) => s.view);
  const isRailVisible = (id: RailItemId) =>
    isRailItemVisibleForShareViewer(id, isReadOnly, shareView);

  // Auto-save disabled — editor runs locally for best performance

  // ── Theme ──────────────────────────────────────────────────────────────────
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('sl_theme') as 'dark' | 'light') || 'light';
  });

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    localStorage.setItem('sl_theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  // ── Auto-show project select if logged in and no project loaded ───────────
  useEffect(() => {
    if (isAuthenticated && !meta.siteId) {
      setShowProjectSelect(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── P0.8: Conflict resolution dialog ───────────────────────────────────────
  const conflictShownRef = useRef(false);
  useEffect(() => {
    if (meta.saveStatus !== 'conflict') {
      conflictShownRef.current = false;
      return;
    }
    if (conflictShownRef.current) return;
    conflictShownRef.current = true;

    const { siteId, siteName, clientName } = meta;
    if (!siteId) return;

    useUIStore.getState().showConfirm({
      title: 'Save Conflict',
      message: 'This project was modified by another session. Your local changes could not be saved.\n\n\u2022 Reload \u2014 discard local changes, load server version\n\u2022 Overwrite \u2014 re-save your local changes to the server',
      confirmLabel: 'Reload from Server',
      cancelLabel: 'Overwrite with My Changes',
      variant: 'default',
      onConfirm: async () => {
        try {
          await useProjectStore.getState().loadProjectFromServer(siteId, siteName || '', clientName);
          useUIStore.getState().addNotification({ type: 'info', title: 'Project reloaded from server' });
        } catch (err) {
          useUIStore.getState().addNotification({ type: 'error', title: 'Reload failed', message: err instanceof Error ? err.message : 'Unknown error' });
        }
      },
      onCancel: async () => {
        try {
          // Fetch latest version number from server (GET only — local data is preserved)
          const latest = await fetchProject(siteId);
          // Clear conflict and update to server's latest version
          useProjectStore.getState().setMeta({ version: latest.version, saveStatus: 'saving' });
          // Build payload from local store (user's working copy)
          const localData = useProjectStore.getState().exportProject();
          // Save with If-Match = latest server version (respects optimistic concurrency)
          const result = await saveProject(siteId, localData as unknown as Record<string, unknown>, latest.version);
          if ('conflict' in result) {
            // Another concurrent save happened between our GET and PUT
            useProjectStore.getState().setMeta({ saveStatus: 'conflict' });
            conflictShownRef.current = false;
            useUIStore.getState().addNotification({ type: 'warning', title: 'Conflict persists', message: 'Another save occurred. Try again.' });
            return;
          }
          useProjectStore.getState().setMeta({ version: result.version, saveStatus: 'saved' });
          useProjectStore.getState().markSaved();
          useUIStore.getState().addNotification({ type: 'success', title: 'Changes saved (overwrite)' });
          setTimeout(() => {
            if (useProjectStore.getState().meta.saveStatus === 'saved') {
              useProjectStore.getState().setMeta({ saveStatus: 'idle' });
            }
          }, 3000);
        } catch (err) {
          useProjectStore.getState().setMeta({ saveStatus: 'error' });
          useUIStore.getState().addNotification({ type: 'error', title: 'Overwrite failed', message: err instanceof Error ? err.message : 'Unknown error' });
        }
      },
    });
  }, [meta.saveStatus, meta.siteId, meta.siteName, meta.clientName]);

  // ── Active tickets (in uiStore so LayersPanel can react) ──────────────────
  const handleLeftRailClick = (id: string) => {
    if (id === 'calculator') { openPanel('calculatorPanel'); return; }
    if (id === 'tasks')      { openPanel('tasksPanel');      return; }
    if (id === 'copilot')    { openPanel('copilotPanel');    return; }
    setLeftActiveTicket(id);
  };

  const handleRightRailClick = (id: string) => {
    setRightActiveTicket(id);
  };

  // ── Ticket header info ─────────────────────────────────────────────────────
  const leftTicketInfo = leftActiveTicket
    ? LEFT_RAIL.find((r) => r.id === leftActiveTicket)
    : null;

  const rightTicketInfo = rightActiveTicket === 'detection'
    ? { id: 'detection', icon: <IconRadar />, label: 'Fault Detection' }
    : rightActiveTicket === 'dock-mission'
      ? { id: 'dock-mission', icon: <IconDrone />, label: 'Dock Mission Planner' }
      : rightActiveTicket === 'dock-settings'
        ? { id: 'dock-settings', icon: <IconDockSettings />, label: 'Docking Station' }
        : rightActiveTicket
          ? RIGHT_RAIL.find((r) => r.id === rightActiveTicket)
          : null;


  // ── Indication handlers ────────────────────────────────────────────────────
  const handleAddIndicationSave = (formData: IndicationFormData) => {
    const coords = modalData.coordinates as [number, number];
    // Creation core (geometry defaults, properties mapping, persistence) is
    // shared with the quick-add path — see utils/createIndication.ts.
    const indicationId = createManualIndication(coords, formData);

    if (indicationId) {
      const draw = useMapStore.getState().draw;
      if (draw) {
        try { draw.changeMode('direct_select', { featureId: indicationId }); } catch { /* best-effort: ignore if Mapbox Draw rejects the mode change */ }
      }
    }

    closeModal();
    if (indicationId) {
      addNotification({ type: 'success', title: 'Indication added' });
    }
  };

  const handleEditIndicationSave = (formData: IndicationFormData) => {
    // Fields the user actually CHANGED are human judgements — stamp them so
    // re-derives and bulk-accept never silently displace them. Normalized
    // compare: retyping "2" over "02" is not a change.
    const prevInd = useProjectStore.getState().data.indications
      .find((i) => i.id === modalData.indicationId);
    const editedTopoFields = prevInd
      ? (['station', 'inverter', 'mppt', 'dccb', 'string', 'module'] as const).filter((f) => {
          const before = (prevInd.properties[f] ?? '').trim();
          const after = (formData[f] ?? '').trim();
          if (before === after) return false;
          return before === '' || after === '' || !topologyValuesEqual(before, after);
        })
      : [];
    updateIndication(modalData.indicationId as string, {
      type: formData.type as any,
      properties: {
        title: formData.type,
        description: formData.notes,
        captureDate: formData.captureDate,
        station: formData.station,
        inverter: formData.inverter,
        mppt: formData.mppt,
        dccb: formData.dccb,
        string: formData.string,
        module: formData.module,
        ...(editedTopoFields.length > 0 && prevInd
          ? humanEditProvenance(
              prevInd.properties as unknown as Record<string, unknown>,
              editedTopoFields as unknown as string[],
            )
          : {}),
        linkLibrary: formData.linkLibrary,
        urlOverride: formData.urlOverride,
        srtUrl: formData.srtUrl,
        createIssue: formData.createIssue,
        thermalData: formData.thermalData,
        image: formData.image,
        customFields: formData.customFields,
        customTypeName: formData.type === 'Custom' ? (formData.customTypeName?.trim() || undefined) : undefined,
      },
    });
    // A changed capture date may be a new one — register it for the filter.
    useProjectStore.getState().updateAvailableDates();
    closeModal();
    useMapStore.getState().selectFeature(null);
    addNotification({ type: 'success', title: 'Indication updated' });
  };

  const handleEditIndicationDelete = () => {
    removeIndication(modalData.indicationId as string);
    closeModal();
    useMapStore.getState().selectFeature(null);
    addNotification({ type: 'info', title: 'Indication deleted' });
  };

  const handleIndicationCancel = () => {
    closeModal();
    useMapStore.getState().selectFeature(null);
  };

  const getEditCoordinates = (): [number, number] => {
    if (modalData.coordinates) return modalData.coordinates as [number, number];
    const ind = indications.find((i) => i.id === modalData.indicationId);
    if (!ind) return [0, 0];
    if (ind.geometry.type === 'Point') return ind.geometry.coordinates as [number, number];
    if (ind.geometry.type === 'Polygon') {
      return centroidOfPolygon(ind.geometry.coordinates[0] as [number, number][]) || [0, 0];
    }
    return [0, 0];
  };

  // ── Info preview handlers ──────────────────────────────────────────────────
  const previewIndicationId = useMapStore((s) => s.previewIndicationId);
  const mapInstance = useMapStore((s) => s.map);

  const handlePreviewEditFields = (id: string) => {
    useMapStore.getState().setPreviewIndication(null);
    openModal('editIndication', { indicationId: id });
  };

  const handlePreviewDelete = (id: string) => {
    if (window.confirm('Delete this indication? This cannot be undone.')) {
      removeIndication(id);
      useMapStore.getState().setPreviewIndication(null);
      useMapStore.getState().selectFeature(null);
      addNotification({ type: 'info', title: 'Indication deleted' });
    }
  };

  const handlePreviewClose = () => {
    useMapStore.getState().setPreviewIndication(null);
  };

  // Auto-close preview when a modal opens
  useEffect(() => {
    if (activeModal) {
      useMapStore.getState().setPreviewIndication(null);
    }
  }, [activeModal]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="editor-page">

      {/* ── Impersonation banner (Phase 4 SaaS) ── */}
      {isImpersonating && impersonator && (
        <div className="impersonation-banner">
          <span>
            Impersonating <strong>{authUser?.email}</strong> as admin
            {' '}<span className="impersonation-banner__faded">({impersonator.user.email})</span>
          </span>
          <button
            type="button"
            className="impersonation-banner__btn"
            onClick={() => {
              stopImpersonation();
              window.location.assign('/admin');
            }}
          >
            Return to admin
          </button>
        </div>
      )}

      {/* ── Header ── */}
      <header className="editor-header">
        <div className="editor-header__brand">
          <button
            type="button"
            className="editor-header__back"
            onClick={() => navigate('/dashboard')}
            title="Back to dashboard"
            aria-label="Back to dashboard"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <img src={logoImg} alt="SoleadLab" className="brand-logo-img" />
          <span className="brand-version">SoleadLab 2.0</span>
          {meta.siteName && (
            <>
              <span className="brand-separator">/</span>
              <span className="header-project-name">
                {meta.siteName}
                {meta.isModified && <span className="header-modified-dot" title="Unsaved changes" />}
              </span>
            </>
          )}
        </div>

        <div className="editor-header__center" />

        <div className="editor-header__right">
          {meta.siteName && !meta.siteId && isAuthenticated && (
            <button
              className="header-local-pill"
              onClick={() => setShowProjectSelect(true)}
              title="This project is local only. Click to link to a server project."
              type="button"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Local only
            </button>
          )}
          {assetLoading.isActive && (
            <div className="header-status-pill header-status-pill--loading">
              <span className="header-status-dot" />
              Loading assets… {assetLoading.loaded}/{assetLoading.total}
            </div>
          )}
          <div className={`header-status-pill header-status-pill--${meta.saveStatus || 'idle'}`}>
            <span className="header-status-dot" />
            {meta.saveStatus === 'conflict' ? 'Conflict'
              : meta.saveStatus === 'saving' ? 'Saving...'
              : meta.saveStatus === 'saved' ? 'Saved'
              : meta.saveStatus === 'error' ? 'Save Error'
              : meta.saveStatus === 'unsaved' ? 'Unsaved'
              : meta.isModified ? 'Modified'
              : 'Saved'}
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className={`editor-body${(layoutAnchorMode || overlayAnchorMode) ? ' editor-body--anchor-mode' : ''}`}>

        {/* ── Left Rail Wrapper (rail + ticket, centered vertically) ── */}
        <div className={`rail-wrapper rail-wrapper--left${leftRailOpen ? '' : ' rail-wrapper--collapsed'}`}>
          {/* Mobile: rails tuck away to the side edges; the arrow reopens. */}
          <button
            type="button"
            className="rail-toggle rail-toggle--left"
            onClick={() => setLeftRailOpen((v) => !v)}
            aria-label={leftRailOpen ? 'Hide menu' : 'Show menu'}
          >
            {leftRailOpen ? '‹' : '›'}
          </button>
          <nav className="left-rail">
            <div className="rail-logo">
              <div className="rail-logo-dot" />
            </div>

            <div className="rail-items">
              {/* F-02E-2C: rail items are filtered by `isRailVisible`, a
                  pure helper that combines the share-viewer flag with the
                  link's exposed_sections. Owners pass through unchanged.
                  Share visitors see only core display + the optional
                  sections (tasks / iv / state / monitoring) the owner
                  opted-in to expose. Pure-owner surfaces (project, media,
                  copilot, review-tools) are hidden always for visitors. */}
              {LEFT_RAIL.filter((item) => isRailVisible(item.id as RailItemId)).map((item) => (
                <RailItem
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  active={leftActiveTicket === item.id}
                  onClick={() => handleLeftRailClick(item.id)}
                  badge={item.id === 'indications' ? (indications.length || undefined) : undefined}
                />
              ))}
            </div>

            <div className="rail-bottom">
              {currentOrg && (
                <button
                  className="rail-theme-toggle"
                  onClick={() => openPanel('orgSettingsPanel')}
                  title="Organization settings"
                  type="button"
                >
                  <IconBuilding />
                </button>
              )}
              {currentOrg && (
                <button
                  className="rail-theme-toggle"
                  onClick={() => openPanel('teamPanel')}
                  title="Team settings"
                  type="button"
                >
                  <IconUsers />
                </button>
              )}
              {canManageShareLinks && (
                <button
                  className="rail-theme-toggle"
                  onClick={() => openPanel('shareLinksPanel')}
                  title="View-only share links"
                  type="button"
                >
                  <IconShareLink />
                </button>
              )}
              {isAdmin && (
                <button
                  className="rail-theme-toggle"
                  onClick={() => openModal('storageAdmin')}
                  title="Storage Admin"
                  type="button"
                >
                  <IconShield />
                </button>
              )}
              {isAdmin && !isImpersonating && (
                <a
                  className="rail-theme-toggle"
                  href="/admin"
                  title="Admin dashboard"
                >
                  <IconAdminPanel />
                </a>
              )}
              <button
                className="rail-theme-toggle"
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                type="button"
              >
                {theme === 'dark' ? <IconSun /> : <IconMoon />}
              </button>
            </div>
          </nav>

          {/* ── Left Ticket ── */}
          <div className="left-ticket" data-open={leftActiveTicket !== null ? 'true' : 'false'}>
            <div className="ticket-inner">
              <div className="ticket-header">
                {leftTicketInfo ? (
                  <>
                    <span className="ticket-header__icon">{leftTicketInfo.icon}</span>
                    <span className="ticket-header__label">{leftTicketInfo.label}</span>
                  </>
                ) : (
                  <span className="ticket-header__label">Inspection Editor</span>
                )}
                <button
                  className="ticket-close"
                  onClick={() => setLeftActiveTicket(null)}
                  type="button"
                  title="Close panel"
                >
                  ×
                </button>
              </div>
              <div className={`ticket-body${leftActiveTicket === 'indications' ? ' ticket-body--fill' : ''}`}>
                <EditorPanel activeTicket={leftActiveTicket} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Layout Anchor Mode (left pane in split-screen) ── */}
        {layoutAnchorMode && <LayoutAnchorMode />}
        {overlayAnchorMode && <OverlayAnchorMode />}

        {/* ── Map Area ── */}
        <div
          className="map-area"
          data-right-ticket={rightActiveTicket !== null ? 'true' : 'false'}
        >
          <MapCanvas />
          <AssetLoadingBar {...assetLoading} />
          {/* Ingest feedback belongs over the map, not inside the Plans
              ticket: the poller is page-wide, so a job was invisible the
              moment any other ticket was open. Self-gates for share viewers. */}
          <DxfIngestBanner />

          {/* Date filter toolbar — floating over map, top center */}
          <DateFilterToolbar />

          {/* Layers panel — always visible */}
          <div className="floating-layers">
            <LayersPanel />
          </div>

          {/* Floating info preview near selected indication */}
          {previewIndicationId && mapInstance && (
            <IndicationPreview
              indicationId={previewIndicationId}
              map={mapInstance}
              onEditFields={handlePreviewEditFields}
              onDelete={handlePreviewDelete}
              onClose={handlePreviewClose}
            />
          )}

          {/* FL-1: field-link write surface (status + notes). Renders null
              for everyone except FIELD-kind share visitors — the global
              read-only viewer contract stays untouched. */}
          {previewIndicationId && (
            <FieldActionsPanel indicationId={previewIndicationId} />
          )}

          {/* FL-2: field-link quick-add (type picker → armed map taps).
              Renders null for everyone except FIELD-kind share visitors. */}
          <FieldAddControl />

          {/* Layout label popup (edit/move/delete) */}
          <LayoutLabelPopup />

          {/* Dock-demo map layers — all mounted unconditionally at EditorPage
              level so they persist regardless of which right-rail ticket is
              currently open. Each component returns null and side-effects
              into the shared Mapbox instance via useMapStore.

              - DockHomeLayer: dock home markers + geofence rings
              - DockMissionRouteLayer: selected mission's waypoints + line +
                home legs
              - DemoMissionMapLayer: imported demo photos + flight path
              - DemoPhotoPopup: viewer for imported demo photos
              - LiveViewMockup: HUD overlay + animated drone marker, only
                renders when live view is open */}
          {/* F-03E-5: dock-demo map layers are gated off for share viewers.
              `DockHomeLayer` and `DockMissionRouteLayer` would otherwise
              render visitor-created phantom dock home pins / mission route
              lines on the shared map, which is consistent with hiding the
              Dock Mission Planner + Docking Station rail surfaces. The
              other demo-flow layers stay mounted because they are gated
              by their own internal `useDockDemoStore` state — visitor
              never has imported demo media, so they render nothing
              anyway. */}
          {!isReadOnly && <DockHomeLayer />}
          {!isReadOnly && <DockMissionRouteLayer />}
          <DemoMissionMapLayer />
          <DemoPhotoPopup />
          <LiveViewMockup />
          {/* Per-leg overlay: midpoint markers (gimbal direction arrow +
              tilt label + REC/interval badges) with click-to-popup and
              drag-to-rotate gimbal yaw. Also renders dotted interval-
              capture preview points along each leg with interval shooting. */}
          <LegOverlayLayer />

          {/* Per-dock no-fly zones — translucent red fills + dashed outlines
              with an in-progress draft polygon during draw mode. Mounted at
              page level (same rationale as the other dock-demo layers) so
              NFZs are visible regardless of which right-rail ticket is open. */}
          <NoFlyZoneLayer />

          {/* Right-click context menu for waypoint markers. Renders a DOM
              popover at the click point with delete / insert before / insert
              after / duplicate / copy & paste leg params actions. Driven by
              the store's contextMenuTarget; closed by outside click or Esc. */}
          <WaypointContextMenu />

          {/* Live preview of the scan-pattern wizard's in-progress polygon /
              line / vertex dots. Goes empty once the user finalizes or
              cancels — the generated sweep waypoints then render through
              DockMissionRouteLayer like any other waypoints. */}
          <ScanDraftLayer />

          {/* Map-anchored altitude + heading quick-edit popup for the
              SELECTED waypoint (set by clicking a marker). Renders an
              attached Mapbox Marker hosting a portal; closes on outside
              click or Esc. */}
          <WaypointMapEditor />

          {/* Per-leg camera coverage footprint patches — translucent blue
              ellipses on the ground showing roughly what the camera sees
              on each leg. v1 approximation; renders under the route line
              so waypoints + cyan dashed line stay clearly readable. */}
          <CameraCoverageLayer />

          {/* Mission lifecycle (mock send-to-operator flow):
              - MissionFlowController drives time-based state transitions
                (pending → scheduled → in_flight) via a 1-Hz interval.
              - DockMissionStatusBadge attaches mapboxgl.Markers at bound
                dock home points showing flashing/countdown badges while
                a mission is awaiting operator approval or scheduled. */}
          <MissionFlowController />
          <DockMissionStatusBadge />

          {/* On mission completion, auto-import the mission's Drive folder
              (mockDataFolderUrl) so the photos + flight path land on the
              map. Idempotent via mockDataImportedAt; re-sending clears the
              stamp for a fresh import. */}
          <MissionAutoImportController />

          {/* Per-project scoping: swaps dockDemoStore's localStorage bucket
              when projectStore.meta.siteId changes so missions / docks /
              imported data are strictly isolated across project switches. */}
          <DockDemoProjectScopeController />

          {/* Draw mode indicator */}
          {isDrawing && (
            <div className="draw-mode-overlay">
              <span className="draw-mode-indicator">
                ✏ Draw Mode: {drawMode.replace(/_/g, ' ')}
              </span>
            </div>
          )}

          {/* Tasks panel — floating overlay */}
          {panelStates.tasksPanel?.isOpen && (
            <TasksPanel onClose={() => closePanel('tasksPanel')} />
          )}

          {/* Photo classification panel — floating overlay (PFC-1A) */}
          {panelStates.photoClassifierPanel?.isOpen && (
            <PhotoClassificationPanel onClose={() => closePanel('photoClassifierPanel')} />
          )}

          {/* Calculator panel — floating overlay */}
          {panelStates.calculatorPanel?.isOpen && (
            <CalculatorPanel onClose={() => closePanel('calculatorPanel')} />
          )}

          {/* Mobile uploads panel — floating overlay */}
          {panelStates.mobileUploadsPanel?.isOpen && (
            <MobileUploadsPanel onClose={() => closePanel('mobileUploadsPanel')} />
          )}

          {/* IV Reporting panel — floating overlay */}
          {panelStates.ivPanel?.isOpen && (
            <IVPanel onClose={() => closePanel('ivPanel')} />
          )}

          {/* Site State panel — floating overlay */}
          {panelStates.statePanel?.isOpen && (
            <StatePanel onClose={() => closePanel('statePanel')} />
          )}

          {/* Score panel — floating overlay (D2.4) */}
          {panelStates.scoringPanel?.isOpen && (
            <ScorePanel onClose={() => closePanel('scoringPanel')} />
          )}

          {/* Copilot panel — floating overlay (E2.3) */}
          {panelStates.copilotPanel?.isOpen && (
            <CopilotPanel onClose={() => closePanel('copilotPanel')} />
          )}

          {/* Team panel — floating overlay (Phase 4 SaaS) */}
          {panelStates.teamPanel?.isOpen && (
            <TeamPanel onClose={() => closePanel('teamPanel')} />
          )}

          {/* Organization settings panel — floating overlay (Phase 4 SaaS) */}
          {panelStates.orgSettingsPanel?.isOpen && (
            <OrgSettingsPanel onClose={() => closePanel('orgSettingsPanel')} />
          )}

          {/* View-only share links panel — floating overlay (F-02E-1) */}
          {panelStates.shareLinksPanel?.isOpen && (
            <ShareLinkManagerPanel
              siteId={meta.siteId ?? null}
              siteName={meta.siteName ?? null}
              onClose={() => closePanel('shareLinksPanel')}
            />
          )}

        </div>

        {/* ── Right Rail Wrapper (ticket + rail, centered vertically) ── */}
        <div className={`rail-wrapper rail-wrapper--right${rightRailOpen ? '' : ' rail-wrapper--collapsed'}`}>
          <button
            type="button"
            className="rail-toggle rail-toggle--right"
            onClick={() => setRightRailOpen((v) => !v)}
            aria-label={rightRailOpen ? 'Hide menu' : 'Show menu'}
          >
            {rightRailOpen ? '›' : '‹'}
          </button>
          {/* ── Right Ticket ── */}
          <div
            className="right-ticket"
            data-open={rightActiveTicket !== null ? 'true' : 'false'}
            data-ticket-id={rightActiveTicket ?? ''}
          >
            <div className="ticket-inner">
              <div className="ticket-header">
                {rightTicketInfo ? (
                  <>
                    <span className="ticket-header__icon">{rightTicketInfo.icon}</span>
                    <span className="ticket-header__label">{rightTicketInfo.label}</span>
                  </>
                ) : (
                  <span className="ticket-header__label">Monitoring Builder</span>
                )}
                <button
                  className="ticket-close"
                  onClick={() => setRightActiveTicket(null)}
                  type="button"
                  title="Close panel"
                >
                  ×
                </button>
              </div>
              <div className="ticket-body">
                {/* F-03E-5: panel-mount defense — if a share viewer
                    somehow lands with rightActiveTicket=dock-mission or
                    dock-settings (e.g. stale URL/deep-link), render
                    nothing rather than the full demo editor. Authenticated
                    owners/operators continue to see the panels normally. */}
                {rightActiveTicket === 'detection' ? (
                  // Detection ticket: same panel-mount defense as the dock
                  // panels — share viewers / non-operators get nothing even
                  // if the ticket id lands via a stale deep-link. The panel
                  // repeats this predicate internally (defense in depth).
                  isRailVisible('detection') && canRunDetection ? <DetectionPanel /> : null
                ) : rightActiveTicket === 'dock-mission' ? (
                  isRailVisible('dock-mission') ? <DockMissionDemoPanel /> : null
                ) : rightActiveTicket === 'dock-settings' ? (
                  isRailVisible('dock-settings') ? <DockStationsDemoPanel /> : null
                ) : (
                  <MonitoringPanel activeTicket={rightActiveTicket} />
                )}
              </div>
            </div>
          </div>

          <nav className="right-rail">
            <div className="rail-logo">
              <div className="rail-logo-dot rail-logo-dot--green" />
            </div>

            <div className="rail-items">
              {/* F-02E-2C: share visitors only see monitoring rails when
                  'monitoring' is exposed; data-source/sld/server-sync are
                  owner write surfaces and stay hidden in share mode. */}
              {RIGHT_RAIL.filter((item) => isRailVisible(item.id as RailItemId)).map((item) => (
                <RailItem
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  active={rightActiveTicket === item.id}
                  onClick={() => handleRightRailClick(item.id)}
                  badge={item.id === 'alarms' ? (((Array.isArray(activeAlarms) ? activeAlarms.length : 0) + (Array.isArray(triggeredAdvancedRules) ? triggeredAdvancedRules.length : 0)) || undefined) : undefined}
                />
              ))}
            </div>
          </nav>

          {/* ── Detection Rail (separate surface below the monitoring rail,
                  dock-rail pattern). One icon: the Fault Detection ticket —
                  scan dispatch + candidate review, replacing the retired
                  floating ScanButton. Hidden for share viewers
                  (isRailVisible) and for roles below operator
                  (canRunDetection), mirroring the backend detections gate. */}
          {isRailVisible('detection') && canRunDetection && (
            <nav className="right-rail right-rail--detect">
              <div className="rail-items">
                <RailItem
                  icon={<IconRadar />}
                  label="Fault Detection"
                  active={rightActiveTicket === 'detection'}
                  onClick={() => handleRightRailClick('detection')}
                />
              </div>
            </nav>
          )}

          {/* ── Dock Mission Rail (separate surface below the monitoring rail).
                  Holds dock-related icons; grows independently of monitoring.

                  F-03E-5: each button is gated through `isRailVisible` for
                  symmetry with RIGHT_RAIL. Share viewers see neither the
                  Dock Mission Planner nor the Docking Station rail icon;
                  authenticated owners/operators see both. If both icons
                  end up hidden, the empty <nav> still renders but has no
                  visible children (the CSS column collapses). */}
          <nav className="right-rail right-rail--dock">
            <div className="rail-items">
              {isRailVisible('dock-mission') && (
                <RailItem
                  icon={<IconDrone />}
                  label="Dock Mission Planner"
                  active={rightActiveTicket === 'dock-mission'}
                  onClick={() => handleRightRailClick('dock-mission')}
                />
              )}
              {isRailVisible('dock-settings') && (
                <RailItem
                  icon={<IconDockSettings />}
                  label="Docking Station"
                  active={rightActiveTicket === 'dock-settings'}
                  onClick={() => handleRightRailClick('dock-settings')}
                />
              )}
            </div>
          </nav>
        </div>

      </div>

      {/* ── Add Indication Modal ── */}
      {/* F-02E-2B: never mount the add/edit indication modals for share
          viewers. EditorPanel's add-mode toggle is hidden in
          IndicationsTicket and the IndicationPreview's Edit button is
          hidden, so this branch is normally unreachable in share mode —
          but this gate is defence in depth against any future code path
          (e.g. keyboard shortcut, deep-link) that might set activeModal. */}
      {activeModal === 'addIndication' && !!modalData.coordinates && !isReadOnly && (
        <>
          <div className="indication-popup-backdrop" onClick={handleIndicationCancel} />
          <IndicationPopup
            mode="add"
            coordinates={modalData.coordinates as [number, number]}
            onSave={handleAddIndicationSave}
            onCancel={handleIndicationCancel}
          />
        </>
      )}

      {/* ── Edit Indication Modal ── */}
      {activeModal === 'editIndication' && !!modalData.indicationId && !isReadOnly && (
        <>
          <div className="indication-popup-backdrop" onClick={handleIndicationCancel} />
          <IndicationPopup
            mode="edit"
            coordinates={getEditCoordinates()}
            indicationId={modalData.indicationId as string}
            onSave={handleEditIndicationSave}
            onCancel={handleIndicationCancel}
            onDelete={handleEditIndicationDelete}
          />
        </>
      )}

      {/* ── Photo Viewer Modal ── */}
      {activeModal === 'photoViewer' && <PhotoViewerModal />}

      {/* ── Data Point Config Modal (new / edit) ── */}
      {activeModal === 'editDataPoint' && (
        <DataPointConfigModal
          isOpen={true}
          onClose={closeModal}
          datapointId={modalData.isNew ? undefined : (modalData.datapointId as string | undefined)}
          initialCoordinates={modalData.coordinates as [number, number] | undefined}
        />
      )}

      {/* ── Advanced Rule Modal (new / edit) ── */}
      {activeModal === 'advancedRule' && <AdvancedRuleModal />}

      {/* ── SLD Viewer Modal ── */}
      {activeModal === 'sldViewer' && <SLDViewerModal />}

      {/* ── Google Drive Import Modal ── */}
      {activeModal === 'driveImport' && <DriveImportModal />}

      {/* ── Storage Admin Modal (admin only) ── */}
      {activeModal === 'storageAdmin' && <StorageAdminModal />}

      {/* ── Layout Import Modal ── */}
      {activeModal === 'layoutImport' && <LayoutImportModal />}

      {/* ── Classification Config Modal ── */}
      {activeModal === 'classificationConfig' && <ClassificationConfigModal />}

      {/* ── Photo Registration Wizard (panel-based, rendered via portal) ── */}
      {panelStates.photoRegPanel?.isOpen && (
        <PhotoRegistrationWizard onClose={() => closePanel('photoRegPanel')} />
      )}

      {/* ── Project Selection Modal ── */}
      <ProjectSelectModal
        isOpen={showProjectSelect}
        onClose={() => setShowProjectSelect(false)}
      />

      {/* ── Progress Overlay (Save/Open operations) ── */}
      <ProgressOverlay />

      {/* ── Confirm Dialog (global, driven by uiStore) ── */}
      <ConfirmDialog />

      {/* ── Image Upload Options Popup (global, driven by uiStore) ── */}

      {/* ── Notification Toasts ── */}
      <NotificationToasts />

    </div>
  );
}
